import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Pagination,
  TableSortLabel
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faCheckCircle, 
  faTrash, 
  faBolt, 
  faDroplet,
  faExclamationTriangle,
  faSignal
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { estimateLeakLitresPerHour, formatLeakMessageWithSize } from '../../utils/leakEstimate';

/**
 * Compact table-based notification list for better information density
 */
const NotificationTable = ({ notifications, onUpdateStatus, onDelete, page, totalPages, onPageChange, compact = false, showDelete = true, deviceLinkState }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState(null); // 'leakSize' | 'serialNumber' | 'deviceName' | null
  const [sortDirection, setSortDirection] = useState('asc');

  // Format date to short format
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), compact ? 'dd/MM/yy HH:mm' : 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get icon for notification type
  const getTypeIcon = (type, unit) => {
    switch (type) {
      case 'threshold_exceeded':
        return unit?.toLowerCase() === 'kwh' ? faBolt : faDroplet;
      case 'leak_detected':
        return faExclamationTriangle;
      case 'signal_strength':
        return faSignal;
      default:
        return faExclamationTriangle;
    }
  };

  // Get color for notification type
  const getTypeColor = (type, unit) => {
    if (type === 'threshold_exceeded') {
      return unit?.toLowerCase() === 'kwh' ? theme.palette.warning.main : theme.palette.primary.main;
    }
    return theme.palette.error.main;
  };

  // Get status chip
  const getStatusChip = (status) => {
    const configs = {
      new: { color: 'error', label: 'New' },
      viewed: { color: 'warning', label: 'Viewed' },
      resolved: { color: 'success', label: 'Resolved' }
    };
    const config = configs[status] || { color: 'default', label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  // Get type label
  const getTypeLabel = (type) => {
    const labels = {
      threshold_exceeded: 'Threshold',
      leak_detected: 'Leak',
      signal_strength: 'Signal'
    };
    return labels[type] || type;
  };

  // Handle row click to navigate to device
  const handleRowClick = (notification) => {
    if (notification.device?.id) {
      navigate(`/devices/${notification.device.id}`, deviceLinkState ? { state: deviceLinkState } : undefined);
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection(key === 'leakSize' ? 'desc' : 'asc');
  };

  const sortedNotifications = useMemo(() => {
    const list = Array.isArray(notifications) ? [...notifications] : [];
    if (!sortBy) return list;

    const getSortValue = (notification) => {
      if (sortBy === 'leakSize') {
        const est = estimateLeakLitresPerHour(notification);
        return est == null ? -1 : Number(est);
      }
      if (sortBy === 'serialNumber') {
        return String(notification?.device?.serialNumber || '').toLowerCase();
      }
      if (sortBy === 'deviceName') {
        return String(notification?.device?.description || '').toLowerCase();
      }
      return '';
    };

    list.sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);

      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }

      if (cmp === 0) {
        const aTs = new Date(a?.detectedAt || a?.createdAt || 0).getTime();
        const bTs = new Date(b?.detectedAt || b?.createdAt || 0).getTime();
        cmp = aTs - bTs;
      }

      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [notifications, sortBy, sortDirection]);

  return (
    <Box>
      <TableContainer component={Paper} elevation={compact ? 0 : 2} sx={{ width: '100%' }}>
        <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '40px' : '50px', p: compact ? 0.5 : 2 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '80px' : '120px', p: compact ? 0.5 : 2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '100px' : '140px', p: compact ? 0.5 : 2 }}>
                <TableSortLabel
                  active={sortBy === 'serialNumber'}
                  direction={sortBy === 'serialNumber' ? sortDirection : 'asc'}
                  onClick={() => handleSort('serialNumber')}
                >
                  Device S/N
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', p: compact ? 0.5 : 2 }}>
                <TableSortLabel
                  active={sortBy === 'deviceName'}
                  direction={sortBy === 'deviceName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('deviceName')}
                >
                  Device Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '90px' : '140px', p: compact ? 0.5 : 2 }}>
                <TableSortLabel
                  active={sortBy === 'leakSize'}
                  direction={sortBy === 'leakSize' ? sortDirection : 'desc'}
                  onClick={() => handleSort('leakSize')}
                >
                  Possible Leak/h
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', p: compact ? 0.5 : 2 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '100px' : '140px', p: compact ? 0.5 : 2 }}>Detected</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: compact ? '80px' : '150px', textAlign: 'center', p: compact ? 0.5 : 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedNotifications.map((notification) => {
              const unit = notification?.data?.unit || 'L';
              const typeColor = getTypeColor(notification.type, unit);
              const estLitresPerHour = estimateLeakLitresPerHour(notification);
              const messageWithLeakSize = formatLeakMessageWithSize(notification);
              const deviceName = notification?.device?.description || '-';
              const location = notification?.device?.location || notification?.site?.description || '';
              const deviceNameWithLocation = location
                ? `${deviceName === '-' ? '' : deviceName} ${deviceName === '-' ? '' : '—'} ${location}`.trim()
                : deviceName;
              
              return (
                <TableRow
                  key={notification.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    borderLeft: `4px solid ${typeColor}`,
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                  onClick={() => handleRowClick(notification)}
                >
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Tooltip title={getTypeLabel(notification.type)}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: compact ? 24 : 32,
                          height: compact ? 24 : 32,
                          borderRadius: 1,
                          backgroundColor: `${typeColor}20`,
                          color: typeColor
                        }}
                      >
                        <FontAwesomeIcon icon={getTypeIcon(notification.type, unit)} size={compact ? 'xs' : 'sm'} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    {getStatusChip(notification.status)}
                  </TableCell>
                  
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Typography variant={compact ? 'caption' : 'body2'} sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                      {notification.device?.serialNumber || 'N/A'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Typography variant={compact ? 'caption' : 'body2'} noWrap sx={{ maxWidth: compact ? 120 : 180 }}>
                      {deviceNameWithLocation || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Typography
                      variant={compact ? 'caption' : 'body2'}
                      sx={{ color: estLitresPerHour != null ? 'error.main' : 'text.secondary', fontWeight: estLitresPerHour != null ? 600 : 400 }}
                    >
                      {estLitresPerHour != null ? `${estLitresPerHour.toLocaleString('en-US')} L/hour` : '-'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Typography variant={compact ? 'caption' : 'body2'} noWrap={compact} sx={{ maxWidth: compact ? 200 : 'none' }}>
                      {messageWithLeakSize}
                    </Typography>
                    {!compact && notification.data?.threshold && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Threshold: {notification.data.threshold} {unit}
                        {notification.data.current !== undefined && ` | Current: ${notification.data.current} ${unit}`}
                      </Typography>
                    )}
                    {(() => {
                      const hours = Number(notification?.data?.continuousNightHours) || 0;
                      if (hours <= 0) return null;
                      return (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          <Chip size="small" variant="outlined" label={`${hours}h continuous`} />
                        </Box>
                      );
                    })()}
                  </TableCell>
                  
                  <TableCell sx={{ p: compact ? 0.5 : 2 }}>
                    <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" noWrap>
                      {formatDate(notification.detectedAt || notification.createdAt)}
                    </Typography>
                  </TableCell>
                  
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ p: compact ? 0.5 : 2 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {notification.status !== 'viewed' && notification.status !== 'resolved' && (
                        <Tooltip title="Mark as Viewed">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onUpdateStatus(notification.id, 'viewed')}
                          >
                            <FontAwesomeIcon icon={faEye} size="xs" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {notification.status !== 'resolved' && (
                        <Tooltip title="Mark as Resolved">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => onUpdateStatus(notification.id, 'resolved')}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} size="xs" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {showDelete && !compact && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(notification.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} size="xs" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => onPageChange(newPage)}
            color="primary"
            showFirstButton
            showLastButton
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

NotificationTable.propTypes = {
  notifications: PropTypes.array.isRequired,
  onUpdateStatus: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  showDelete: PropTypes.bool,
  deviceLinkState: PropTypes.object
};

export default NotificationTable;
